// /app/frontend/src/components/frugal-v2/components/ZenLockConfigModal.jsx
// Zen Lock configuration modal - i18n compliant

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Lock, Clock, Key, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ZenLockConfigModal = ({ isOpen, onClose, config, onUpdate, t }) => {
  const [enabled, setEnabled] = useState(false);
  const [timeout, setTimeout] = useState(5);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPinFields, setShowPinFields] = useState(false);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setTimeout(config.timeout_minutes || 5);
      setShowPinFields(!config.has_pin);
    }
  }, [config]);

  const handleSave = async () => {
    if (showPinFields && pin) {
      if (pin.length < 4 || pin.length > 6) {
        toast.error(t('frugal.zenLock.pinLength'));
        return;
      }
      if (!/^\d+$/.test(pin)) {
        toast.error(t('frugal.zenLock.pinDigits'));
        return;
      }
      if (pin !== confirmPin) {
        toast.error(t('frugal.zenLock.pinMismatch'));
        return;
      }
    }

    setSaving(true);
    const updateData = { enabled, timeout_minutes: timeout };
    if (showPinFields && pin) updateData.pin = pin;

    const success = await onUpdate(updateData);
    setSaving(false);

    if (success) {
      toast.success(t('frugal.zenLock.saved'));
      onClose();
    } else {
      toast.error(t('frugal.zenLock.error'));
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 rounded-2xl w-full max-w-sm border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            <h2 className="text-base font-semibold text-white">{t('frugal.zenLock.configTitle')}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-400" />
              <span className="text-white text-sm">{t('frugal.zenLock.enable')}</span>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-600'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {enabled && (
            <>
              {/* Timeout */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <Clock className="w-3 h-3" />
                  <span>{t('frugal.zenLock.timeout')}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 3, 5, 10].map(min => (
                    <button
                      key={min}
                      onClick={() => setTimeout(min)}
                      className={`py-1.5 rounded-lg text-xs font-medium ${
                        timeout === min ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-300'
                      }`}
                    >
                      {min}m
                    </button>
                  ))}
                </div>
              </div>

              {/* PIN */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Key className="w-3 h-3" />
                    <span>{t('frugal.zenLock.pin')}</span>
                  </div>
                  {config?.has_pin && !showPinFields && (
                    <button onClick={() => setShowPinFields(true)} className="text-xs text-emerald-400">
                      {t('frugal.zenLock.changePin')}
                    </button>
                  )}
                </div>
                
                {(showPinFields || !config?.has_pin) ? (
                  <div className="space-y-2">
                    <input type="password" inputMode="numeric" maxLength={6}
                      placeholder={t('frugal.zenLock.newPin')}
                      value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    />
                    <input type="password" inputMode="numeric" maxLength={6}
                      placeholder={t('frugal.zenLock.confirmPin')}
                      value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                ) : (
                  <p className="text-emerald-400 text-xs flex items-center gap-1">
                    <Shield className="w-3 h-3" /> {t('frugal.zenLock.pinSet')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/10">
          <button 
            onClick={handleSave} 
            disabled={saving || (enabled && showPinFields && !pin)}
            className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {saving ? t('frugal.zenLock.saving') : t('frugal.zenLock.save')}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ZenLockConfigModal;
